import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { cleanAllSelection, getZoneCatByZoneArea, zoneCatByZoneArea, zoneDataByZoneCat } from "./court.data";
import { DISABLED_DATA as TOKEN_STATE } from "./court.data";
import { DISABLED_COLOR, MY_PURCHASED_COLOR, SELECTED_COLOR, SELECTED_CUBE_COLOR, DISABLED_CUBE_COLOR, MY_PURCHASED_CUBE_COLOR } from "../../../shared/Modals/PurchaseTokenModal/purchaseTokenModal.data";
import { PURCHASE_TYPE } from "../../../../lib/constants/globalConstant";
import { removeTokenFromCart, saveToCart } from "../../../../redux/slices/cartSlice";
import CourtInnerSvg from "./CourtInnerSvg";
import CourtTokens from "./CourtTokens";
import { getCurrentTimestamps } from "../../../../lib/utils/dateAndTimeFormatting";

const InnerCourt = forwardRef(({ viewBox, isPanningRef, selectedTokens, setSelectedTokens }, svgRef) => {
  const dispatch = useDispatch();
  const loadedZones = useRef(false);
  const isDragging = useRef(false);
  const mouseStart = useRef({ x: 0, y: 0, time: 0 });

  const { sportsSeasonId } = useParams();
  const { currentSeason } = useSelector(state => state.seasons);
  const { allTokens, zoneTokensFetched } = useSelector(state => state.sport);
  
  // this is for current-users token-positions
  // const [selectedTokens, setSelectedTokens] = useState([]);

  // call toggleTokenSelection from within your add/remove handlers.
  const toggleTokenSelection = useCallback((token, action, color) => {
    setSelectedTokens((prevTokens) => {
      const updatedTokens = [...prevTokens];
      const tokenIndex = updatedTokens.findIndex((t) => t.xAxis === token.xAxis && t.yAxis === token.yAxis);
  
      if (action === "add" && tokenIndex === -1) {
        updatedTokens.push({ ...token, color });
      } else if (action === "remove" && tokenIndex !== -1) {
        updatedTokens.splice(tokenIndex, 1);
      }
  
      return updatedTokens;
    });
  }, [setSelectedTokens]);

  // handle add to cart functionality
  const handleAddToCart = async ({ token, svgZone, updateFunction }) => {
    if (!token || !svgZone) return;

    let zoneCat = getZoneCatByZoneArea(svgZone);
    let zoneData = zoneDataByZoneCat(zoneCat, currentSeason?.zoneCategories);
    const zonesArray = zoneData?.zones || [];
    const indexInZoneCatByZoneName = zoneCatByZoneArea[zoneCat]?.indexOf(svgZone);
    
    // Safety check for overflow zones
    const safeIndex = (indexInZoneCatByZoneName !== -1) ? (indexInZoneCatByZoneName % (zonesArray.length || 1)) : 0;
    const { zoneName } = zonesArray[safeIndex] || {};

    const data = {
      // tokenId: null, // token-id
      seasonId: sportsSeasonId, // season id
      zoneCategoryName: zoneData?.zoneCategoryName, // Platinum, Gold, Silver
      zoneCategoryColor: zoneData?.zoneCategoryColor, // zone-color
      zoneName, // zone A, B, C, D, ...
      typeOfPurchase: PURCHASE_TYPE.SEASON, // type of purchase
      matchIds: [],
      tokenData: [token],
    };

    const saveToCartResponse = await dispatch(saveToCart(data));

    if(saveToCart.fulfilled.match(saveToCartResponse)) {
      if(updateFunction){
        updateFunction();
      }
      
      toggleTokenSelection(token, "add", SELECTED_CUBE_COLOR);
    }
  };

  // handle remove from cart functionality
  const handleRemoveFromCart = async({ token, svgZone, updateFunction }) => {
    if (!token || !svgZone) return;

    let zoneCat = getZoneCatByZoneArea(svgZone);
    let zoneData = zoneDataByZoneCat(zoneCat, currentSeason?.zoneCategories);
    const zonesArray = zoneData?.zones || [];
    const indexInZoneCatByZoneName = zoneCatByZoneArea[zoneCat]?.indexOf(svgZone);

    // Safety check for overflow zones
    const safeIndex = (indexInZoneCatByZoneName !== -1) ? (indexInZoneCatByZoneName % (zonesArray.length || 1)) : 0;
    const { zoneName } = zonesArray[safeIndex] || {};

    const data = {
      seasonId: sportsSeasonId, // season id
      zoneCategoryName: zoneData?.zoneCategoryName, // Platinum, Gold, Silver
      zoneName, // zone A, B, C, D, ...
      tokenData: [token],
    }

    const removeFromCartResponse = await dispatch(removeTokenFromCart(data));

    if(removeTokenFromCart.fulfilled.match(removeFromCartResponse)) {
      if(updateFunction){
        updateFunction();
      }

      toggleTokenSelection(token, "remove");
    }
  }

  const updateZoneTokensColor = useCallback(async(rect) => {
    const xAxis = rect.getAttribute('dx');
    const yAxis = rect.getAttribute('dy');
    if(!xAxis || !yAxis) return;
          
    const lookForThisCoordinate = allTokens.find(token => token?.xAxis === xAxis && token?.yAxis === yAxis)
    if(!lookForThisCoordinate) return;
    
    const x = rect.getAttribute('x');
    const y = rect.getAttribute('y');

    const token = {
      xAxis,
      yAxis,
      x,
      y,
      dzone: rect.getAttribute('dzone')
    }
          
    // Disable others or add some special color based on requirement
    if(lookForThisCoordinate){
      if(!lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.SEASON && lookForThisCoordinate?.isMyToken){
        // For Entire Season + My Cart
        rect.setAttribute(TOKEN_STATE.myBookedForSeason, 'true');
        rect.setAttribute(TOKEN_STATE.selected, 'true'); // disable
        rect.style.fill = SELECTED_COLOR
        // rect.style.cursor = 'not-allowed'

        toggleTokenSelection(token, "add", SELECTED_CUBE_COLOR);
      }else if(!lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.MATCH && lookForThisCoordinate?.isMyToken){
        // For Some Match/ Matches + My Cart
        rect.setAttribute(TOKEN_STATE.myBookedForSeason, 'true');
        // rect.setAttribute(TOKEN_STATE.disabled, 'true'); // disable-color-switch
        rect.style.fill = SELECTED_COLOR

        toggleTokenSelection(token, "add", SELECTED_CUBE_COLOR);
      }else if(lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.SEASON && lookForThisCoordinate?.isMyToken){
        // For Entire Season + My Ordered
        rect.setAttribute(TOKEN_STATE.myPurchasedForSeason, 'true');
        rect.setAttribute(TOKEN_STATE.disabled, 'true'); // disable
        rect.style.fill = MY_PURCHASED_COLOR
        rect.style.cursor = 'not-allowed'

        toggleTokenSelection({ ...token, disabled: true }, "add", MY_PURCHASED_CUBE_COLOR);
      }else if(lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.MATCH && lookForThisCoordinate?.isMyToken){
        // For Some Match/ Matches + My Ordered
        rect.setAttribute(TOKEN_STATE.myPurchasedForSeason, 'true');
        rect.setAttribute(TOKEN_STATE.disabled, 'true'); // disable-color-switch
        rect.style.fill = MY_PURCHASED_COLOR
        rect.style.cursor = 'not-allowed'

        toggleTokenSelection({ ...token, disabled: true }, "add", MY_PURCHASED_CUBE_COLOR);
      }else if(lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.SEASON && !lookForThisCoordinate?.isMyToken){             
        // For Entire Season + Purchaed by Others
        rect.setAttribute(TOKEN_STATE.purchasedByOtherForSeason, 'true');
        rect.setAttribute(TOKEN_STATE.disabled, 'true'); // disable
        rect.style.fill = DISABLED_COLOR
        rect.style.cursor = 'not-allowed'

        toggleTokenSelection({ ...token, disabled: true }, "add", DISABLED_CUBE_COLOR);
      }else if(lookForThisCoordinate?.ispurchased && lookForThisCoordinate?.purchaseType === PURCHASE_TYPE.MATCH && !lookForThisCoordinate?.isMyToken){               
        // For Some Match/ Matches + Purchaed by Others
        rect.setAttribute(TOKEN_STATE.purchasedByOtherForSeason, 'true');
        rect.setAttribute(TOKEN_STATE.disabled, 'true'); // disable-color-switch
        rect.style.fill = DISABLED_COLOR
        rect.style.cursor = 'not-allowed'

        toggleTokenSelection({ ...token, disabled: true }, "add", DISABLED_CUBE_COLOR);
      }
    }
  }, [allTokens]);
  
  const handleTokenClick = useCallback(async(e) => {
    e?.preventDefault(); // clearing default behaviour

    const rect = e.target;
    const zone = rect.getAttribute('dzone');

    // if disabled, return
    if(rect.getAttribute(TOKEN_STATE.disabled)) return;

    const x = rect.getAttribute('x');
    const y = rect.getAttribute('y');
    // token
    const token = {
      xAxis: rect.getAttribute('dx'),
      yAxis: rect.getAttribute('dy'),
      x,
      y,
      dzone: zone
    }

    const isSelected = rect.getAttribute(TOKEN_STATE.selected);

    const updateFunction = () => {
      if(isSelected) {
        rect.removeAttribute(TOKEN_STATE.selected);
        cleanAllSelection(rect);
      }else {
        rect.setAttribute(TOKEN_STATE.selected, 'true');
        rect.style.fill = SELECTED_COLOR;
      }
    };

    // check if have attribute selected
    if(isSelected){
      await handleRemoveFromCart({ token, svgZone: zone, updateFunction });
    }else {
      await handleAddToCart({ token, svgZone: zone, updateFunction });
    }
  }, [handleRemoveFromCart, handleAddToCart, isPanningRef]);

  const handleCubeClick = useCallback(async(e, token, zone) => {
    e?.preventDefault(); // clearing default behaviour

    const updateFunction = () => {
      try {
        const thisCourtPage = document.getElementById("thisCourtPage");
      
        if(!thisCourtPage) return;
        const rects = thisCourtPage.querySelectorAll('svg rect')
        for(let i=0; i<rects.length; i++) {
          const rect = rects[i];
          const xAxis = rect.getAttribute('dx');
          const yAxis = rect.getAttribute('dy');

          if(!xAxis || !yAxis) continue;

          if(xAxis === token.xAxis && yAxis === token.yAxis){
            rect.removeAttribute(TOKEN_STATE.selected);
            cleanAllSelection(rect);

            return;
          }
        }

      } catch (error) {
        console.log(error);
      }
    }

    await handleRemoveFromCart({ token, svgZone: zone, updateFunction });
  }, [handleRemoveFromCart]);

  // Handle mouse down (initialize position and time)
  const handleMouseDown = useCallback((e) => {

    if (isPanningRef.current) return;
    
    const { clientX, clientY } = e;
    mouseStart.current = { x: clientX, y: clientY, time: getCurrentTimestamps() };
    isDragging.current = false;
  }, [isPanningRef, getCurrentTimestamps]);
  
  // Handle mouse move (track distance and detect drag)
  const handleMouseMove = useCallback((e) => {
    // console.log("moused move - 1");

    if (!mouseStart.current.time) return;
    // console.log("moused move - 2");


    const { clientX, clientY } = e;
    const distance = Math.sqrt(
      Math.pow(clientX - mouseStart.current.x, 2) + Math.pow(clientY - mouseStart.current.y, 2)
    );

    // Drag threshold (e.g., 5-10px)
    if (distance > 10) {
      isDragging.current = true;
    }
  }, []);
  
  // Handle mouse up (determine if click or drag)
  const handleMouseUp = useCallback((e) => {
    // console.log("moused up - 1");

    const timeElapsed = getCurrentTimestamps() - mouseStart.current.time;
    // console.log("moused up - 2");

    // Check if it was a drag (move distance > threshold and time < threshold)
    if (!isDragging.current && timeElapsed < 500) { // 500ms for click threshold
      handleTokenClick(e);  // Call your existing click handler
    }

    // Reset for next action
    mouseStart.current = { x: 0, y: 0, time: 0 };
    isDragging.current = false;
  }, [handleTokenClick, getCurrentTimestamps]);  

  useEffect(() => {    
    const thisCourtPage = document.getElementById("thisCourtPage");
    if(!thisCourtPage || loadedZones.current || !zoneTokensFetched) return;
  
    const rects = thisCourtPage.querySelectorAll('svg rect');
    if(!rects || rects.length === 0) return;
    loadedZones.current = true; // updating ref
  
    rects.forEach(async(rect) => {
      if(!rect || !rect.getAttribute('dx') || !rect.getAttribute('dy')) return;

      rect.style.cursor = 'pointer';
      await updateZoneTokensColor(rect);
      
      // rect.addEventListener("click", handleTokenClick);
      rect.addEventListener("mousedown", handleMouseDown);
      rect.addEventListener("mousemove", handleMouseMove);
      rect.addEventListener("mouseup", handleMouseUp);
    });

    return () => {
      // remove all listeners
      rects.forEach(async(rect) => {
        if(!rect || !rect.getAttribute('dx') || !rect.getAttribute('dy')) return;
        
        // rect.removeEventListener("click", handleTokenClick);
        rect.removeEventListener("mousedown", handleMouseDown);
        rect.removeEventListener("mousemove", handleMouseMove);
        rect.removeEventListener("mouseup", handleMouseUp);
      });
      loadedZones.current = false;
    };
  }, [zoneTokensFetched, updateZoneTokensColor]);

  useEffect(() => {
    const cleanups = [];
    if(!selectedTokens.length) return;
    
    try {
      const thisCourtPage = document.getElementById("thisCourtPage");
      
      if(!thisCourtPage) return;
      
      const selectedCubes = thisCourtPage.querySelectorAll('svg svg')

      // If zones data is fetched then only run so that it will render once only when we have all required data.
      if(selectedCubes.length) {        
        selectedCubes.forEach(async(cube) => {
          const zoneNumber = cube.getAttribute('dzone');
          const token = {
            xAxis: cube.getAttribute('dx'),
            yAxis: cube.getAttribute('dy'),
          }

          const isDisabled = cube.getAttribute('data-disabled');
          if(isDisabled) return;

          const handleClick = (e) => handleCubeClick(e, token, zoneNumber);

          // add listeners
          cube.addEventListener('click', handleClick);

          // cleanup
          cleanups.push(() => {
            cube.removeEventListener('click', handleClick);
          });
        })
      }
    } catch (error) {
        console.log(error)
    }

    return () => {
      // remove all listeners
      cleanups.forEach(cleanup => cleanup());
    };
  }, [selectedTokens])

  return (
    <div id="thisCourtPage">
      <svg
        ref={svgRef}
        viewBox={`${viewBox.x} ${viewBox.y} ${viewBox.w} ${viewBox.h}`}
        fill="none"
        width="100%"
        height="100%"
      >
        <g transform="translate(175, 0)">
          <CourtInnerSvg />
    
          <CourtTokens
            tokens={selectedTokens}
          />
        </g>
      </svg>
    </div>
  );
});

InnerCourt.displayName = "InnerCourt";

export default React.memo(InnerCourt);